import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Plus, Pencil, Trash2, Clock, CalendarDays, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const MONTHS_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const WEEK_ID = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];
const ROLE_LABEL = { both: "Semua", beauty_creator: "Beauty Creator", affiliator: "Affiliator" };
const roleClass = (r) => (r === "affiliator" ? "bg-showty-navy text-white" : r === "beauty_creator" ? "bg-showty-red text-white" : "bg-showty-gold/80 text-white");
const pad = (n) => String(n).padStart(2, "0");
const todayKey = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const isKey = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s || "");
const fmtLong = (key) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key || ""); return m ? `${Number(m[3])} ${MONTHS_ID[Number(m[2]) - 1]} ${m[1]}` : "Pilih tanggal"; };

export default function CalendarPanel({ editable = false }) {
  const [events, setEvents] = useState([]);
  const now = new Date();
  const [view, setView] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [selected, setSelected] = useState(todayKey());
  const jumped = useRef(false);

  const load = useCallback(() => api.get("/calendar").then((r) => setEvents((r.data || []).map((e) => ({ ...e, date: (e.date || "").slice(0, 10) })))).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const byDate = useMemo(() => {
    const map = {};
    events.forEach((e) => { (map[e.date] = map[e.date] || []).push(e); });
    return map;
  }, [events]);

  // Months (of the viewed year) that have a campaign deadline -> shown as markers
  const { campaignMonths, eventMonths } = useMemo(() => {
    const camp = new Set();
    const any = new Set();
    events.forEach((e) => {
      const [y, m] = (e.date || "").split("-");
      if (Number(y) !== view.y || !m) return;
      const idx = Number(m) - 1;
      any.add(idx);
      if (e.source === "campaign") camp.add(idx);
    });
    return { campaignMonths: camp, eventMonths: any };
  }, [events, view.y]);

  // Auto-jump once to the nearest upcoming campaign deadline (fallback: nearest event)
  useEffect(() => {
    if (jumped.current || events.length === 0) return;
    jumped.current = true;
    const tk = todayKey();
    const valid = events.filter((e) => isKey(e.date));
    const upcoming = valid.filter((e) => e.date >= tk).sort((a, b) => a.date.localeCompare(b.date));
    const nearest = upcoming.find((e) => e.source === "campaign") || upcoming[0];
    if (nearest) {
      const [y, m] = nearest.date.split("-").map(Number);
      setView({ y, m: m - 1 });
      setSelected(nearest.date);
    }
  }, [events]);

  const grid = useMemo(() => {
    const first = new Date(view.y, view.m, 1).getDay();
    const total = new Date(view.y, view.m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < first; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(`${view.y}-${pad(view.m + 1)}-${pad(d)}`);
    return cells;
  }, [view]);

  const move = (delta) => setView((v) => { const d = new Date(v.y, v.m + delta, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  const goTo = (dateStr) => {
    if (!isKey(dateStr)) return;
    const [y, m] = dateStr.split("-").map(Number);
    setView({ y, m: m - 1 });
    setSelected(dateStr);
  };
  const selectedEvents = byDate[selected] || [];
  const remove = async (id) => { try { await api.delete(`/calendar/${id}`); load(); } catch (e) { toast.error("Gagal menghapus"); } };

  return (
    <div data-testid="calendar-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-2xl text-showty-navy flex items-center gap-2"><CalendarDays className="w-5 h-5 text-showty-red" /> Calendar</h3>
          <p className="text-sm text-muted-foreground">{editable ? "Buat & atur jadwal / informasi untuk Beauty Creator dan Affiliator." : "Jadwal, deadline campaign & informasi dari tim Showtynion."}</p>
        </div>
        {editable && <EventDialog defaultDate={selected} onDone={load} onSaved={goTo} trigger={<Button className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white" data-testid="calendar-add-btn"><Plus className="w-4 h-4 mr-1" /> Tambah Jadwal</Button>} />}
      </div>

      {/* Month markers strip — red dot = ada deadline campaign */}
      <div className="rounded-2xl border border-border bg-white p-3 mb-4" data-testid="calendar-month-strip">
        <div className="flex items-center justify-between mb-2 px-1">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Bulan {view.y}</span>
          <span className="text-[11px] text-muted-foreground flex items-center gap-2"><span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-showty-red inline-block" /> deadline campaign</span><span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-showty-navy inline-block" /> jadwal lain</span></span>
        </div>
        <div className="grid grid-cols-6 sm:grid-cols-12 gap-1.5">
          {MONTHS_SHORT.map((mm, i) => {
            const hasCampaign = campaignMonths.has(i);
            const hasEvent = eventMonths.has(i);
            const isCur = view.m === i;
            return (
              <button key={mm} onClick={() => setView((v) => ({ ...v, m: i }))} data-testid={`month-chip-${i}`}
                className={`relative rounded-lg py-1.5 text-xs font-medium transition-colors ${isCur ? "bg-showty-navy text-white" : "text-showty-navy/80 hover:bg-showty-pink/40"}`}>
                {mm}
                {hasEvent && <span className={`absolute top-1 right-1.5 w-1.5 h-1.5 rounded-full ${isCur ? "bg-white" : hasCampaign ? "bg-showty-red" : "bg-showty-navy"}`} data-testid={`month-marker-${i}`} />}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-border bg-white p-5" data-testid="calendar-grid">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => move(-1)} className="w-8 h-8 rounded-full hover:bg-showty-pink/40 flex items-center justify-center" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
            <span className="font-display text-lg text-showty-navy">{MONTHS_ID[view.m]} {view.y}</span>
            <button onClick={() => move(1)} className="w-8 h-8 rounded-full hover:bg-showty-pink/40 flex items-center justify-center" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-7 gap-1.5 text-center">
            {WEEK_ID.map((w) => <div key={w} className="text-[11px] font-semibold text-muted-foreground py-1">{w}</div>)}
            {grid.map((key, i) => {
              if (!key) return <div key={`e${i}`} />;
              const evs = byDate[key] || [];
              const hasCampaign = evs.some((e) => e.source === "campaign");
              const isSel = key === selected;
              const isToday = key === todayKey();
              return (
                <button key={key} onClick={() => setSelected(key)}
                  className={`aspect-square rounded-lg border flex flex-col items-center justify-center text-xs relative transition-colors ${isSel ? "border-showty-red bg-showty-pink/30" : isToday ? "border-showty-gold" : "border-border hover:border-showty-red/40"}`}
                  data-testid={`cal-cell-${key}`}>
                  <span className={`font-medium ${isToday ? "text-showty-red" : "text-showty-navy"}`}>{Number(key.split("-")[2])}</span>
                  {evs.length > 0 && <span className={`absolute bottom-1 w-1.5 h-1.5 rounded-full ${hasCampaign ? "bg-showty-red" : "bg-showty-navy/50"}`} />}
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="calendar-day-list">
          <div className="px-5 py-3 border-b border-border">
            <h4 className="font-display text-xl text-showty-navy">{fmtLong(selected)}</h4>
          </div>
          <div className="p-4 space-y-3 max-h-[360px] overflow-y-auto">
            {selectedEvents.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground" data-testid="calendar-day-empty">Tidak ada jadwal di tanggal ini</p>
            ) : selectedEvents.map((e) => (
              <div key={e.id} className="rounded-xl border border-border bg-showty-cream/50 p-4" data-testid={`cal-event-${e.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {e.source === "campaign" && <span className="text-[10px] uppercase tracking-wide font-semibold rounded-full px-2 py-0.5 bg-showty-red text-white flex items-center gap-1" data-testid={`cal-campaign-badge-${e.id}`}><Megaphone className="w-3 h-3" /> Deadline Campaign</span>}
                      <span className={`text-[10px] uppercase tracking-wide font-semibold rounded-full px-2 py-0.5 ${roleClass(e.target_role)}`}>{ROLE_LABEL[e.target_role]}</span>
                      {e.time && <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> {e.time}</span>}
                    </div>
                    <p className="mt-1.5 font-medium text-showty-navy">{e.title}</p>
                    {e.description && <p className="text-sm text-muted-foreground mt-0.5 whitespace-pre-wrap">{e.description}</p>}
                  </div>
                  {editable && e.source !== "campaign" && (
                    <div className="flex gap-1 shrink-0">
                      <EventDialog event={e} onDone={load} onSaved={goTo} trigger={<button className="text-muted-foreground hover:text-showty-navy" data-testid={`cal-edit-${e.id}`}><Pencil className="w-4 h-4" /></button>} />
                      <button onClick={() => remove(e.id)} className="text-muted-foreground hover:text-destructive" data-testid={`cal-delete-${e.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const EventDialog = ({ event, defaultDate, onDone, onSaved, trigger }) => {
  const [open, setOpen] = useState(false);
  const base = { title: "", description: "", date: defaultDate || todayKey(), time: "", target_role: "both" };
  const [f, setF] = useState(event || base);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) setF(event || { ...base, date: defaultDate || todayKey() }); }, [open]);
  const save = async () => {
    if (!f.title) return toast.error("Judul wajib diisi");
    if (!f.date) return toast.error("Tanggal wajib diisi");
    const payload = { title: f.title, description: f.description, date: f.date, time: f.time, target_role: f.target_role };
    try {
      if (event) await api.patch(`/calendar/${event.id}`, payload);
      else await api.post("/calendar", payload);
      toast.success(event ? "Jadwal diperbarui" : "Jadwal ditambahkan");
      setOpen(false);
      onDone?.();
      onSaved?.(f.date);
    } catch (e) { toast.error("Gagal menyimpan jadwal"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="calendar-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl text-showty-navy">{event ? "Edit Jadwal" : "Tambah Jadwal"}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div><Label>Judul / Informasi</Label><Input className="mt-1" value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="calendar-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Tanggal</Label><Input type="date" className="mt-1" value={f.date} onChange={(e) => set("date", e.target.value)} data-testid="calendar-date" /></div>
            <div><Label>Jam (opsional)</Label><Input type="time" className="mt-1" value={f.time} onChange={(e) => set("time", e.target.value)} data-testid="calendar-time" /></div>
          </div>
          <div><Label>Untuk</Label>
            <Select value={f.target_role} onValueChange={(v) => set("target_role", v)}>
              <SelectTrigger className="mt-1" data-testid="calendar-target"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="both">Semua (Creator & Affiliator)</SelectItem>
                <SelectItem value="beauty_creator">Beauty Creator</SelectItem>
                <SelectItem value="affiliator">Affiliator</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Deskripsi</Label><Textarea rows={4} className="mt-1" value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="calendar-desc" /></div>
        </div>
        <DialogFooter><Button onClick={save} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white w-full" data-testid="calendar-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
